import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, exhaustMap, filter, switchMap, take, tap, timeout } from 'rxjs/operators';
import { EMPTY, forkJoin, from, map, of, throwError, withLatestFrom } from 'rxjs';
import {
  addCategory,
  addCategoryFailure,
  addCategorySuccess,
  completePendingFreePlanRegistration,
  createBrand,
  createBrandFailure,
  createBrandSuccess,
  createClient,
  createClientFailure,
  createClientSuccess,
  createDefaultPersonalWheel,
  createLink,
  createLinkFailure,
  createLinkSuccess,
  createWheelForUser,
  createWheelForUserFailure,
  createWheelForUserSuccess,
  deleteCategory,
  deleteCategoryFailure,
  deleteCategorySuccess,
  deleteClient,
  deleteClientFailure,
  deleteClientSuccess,
  deleteLink,
  deleteLinkFailure,
  deleteLinkSuccess,
  deletePersonalWheel,
  deletePersonalWheelFailure,
  deletePersonalWheelSuccess,
  deleteSession,
  deleteSessionFailure,
  deleteSessionSuccess,
  editCategory,
  editCategoryFailure,
  editCategorySuccess,
  getLinkDataByToken,
  getLinkDataByTokenFailure,
  getLinkDataByTokenSuccess,
  getSessionsForParticipant,
  getSessionsForParticipantFailure,
  getSessionsForParticipantSuccess,
  getUserSessions,
  getUserSessionsFailure,
  getUserSessionsSuccess,
  getWheelVersionById,
  getWheelVersionByIdFailure,
  getWheelVersionByIdSuccess,
  loadBrand,
  loadBrandFailure,
  loadBrandSuccess,
  loadClients,
  loadClientsFailure,
  loadClientsSuccess,
  loadDefaultTemplate,
  loadDefaultTemplateSuccess,
  loadUserWheels,
  loadUserWheelsFailure,
  loadUserWheelsSuccess,
  loadWheelForParticipant,
  loadWheelForParticipantFailure,
  loadWheelForParticipantSuccess,
  planLimitReached,
  resetSession,
  saveClientFocus,
  saveClientFocusFailure,
  saveClientFocusForParticipant,
  saveClientFocusForParticipantFailure,
  saveClientFocusForParticipantSuccess,
  saveClientFocusSuccess,
  savePendingFreePlanWheel,
  savePendingFreePlanWheelFailure,
  savePendingFreePlanWheelSuccess,
  saveSession,
  saveSessionFailure,
  saveSessionSuccess,
  setActiveWheel,
  setActiveWheelFailure,
  setScore,
  submitFeatureRequest,
  submitFeatureRequestFailure,
  submitFeatureRequestSuccess,
  submitInquiryFailure,
  submitInquiryRequest,
  submitInquirySuccess,
  submitSupportRequest,
  submitSupportRequestFailure,
  submitSupportRequestSuccess,
  switchPersonalWheel,
  updateBrand,
  updateBrandFailure,
  updateBrandSuccess,
  updateClient,
  updateClientFailure,
  updateClientSuccess,
  updatePersonalWheel,
  updatePersonalWheelFailure,
  updatePersonalWheelSuccess,
  updateWheelOnSaveClientFocusSuccess,
} from './actions';
import { WheelsApiService } from '../services/api/wheels-api.service';
import { ProfessionalApiService } from '../services/api/professional-api.service';
import { SupportService } from '../services/api/support.service';
import wheelTemplateToState from '../utils/mappers/wheel-template-to-state';
import { Store } from '@ngrx/store';
import { selectActiveWheel, selectLang, selectSessions, selectUserWheels } from './selectors';
import { WheelStateCategory } from './typings';
import { mapActiveWheel, mapOutputToState } from '../utils/mappers/active-wheel.mapper';
import { NotificationService } from '../services/notification.service';
import { AppError } from '../core/errors/app-error.model';
import { APP_ERROR_CODE } from '../core/errors/app-error-code';
import { defaultWheelIdByLocale } from '../features/wheel/wheel.constants';
import { SessionSavedDialogComponent } from '../ui/dialogs/session-saved-dialog/session-saved-dialog.component';
import { PendingFreePlanWheelStorageService } from '../services/pending-free-plan-wheel-storage.service';
import { Router } from '@angular/router';
import { RegistrationCompleteDialogComponent } from '../ui/dialogs/registration-complete-dialog/registration-complete-dialog.component';
import { PersonalPlanUpsellDialogComponent } from '../ui/dialogs/personal-plan-upsell-dialog/personal-plan-upsell-dialog.component';
import { resolvePlanLimitUpsellDialogConfig } from './personal-plan-upsell-dialog.config';

@Injectable()
export class WheelEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(HttpClient);
  private wheelsApiService = inject(WheelsApiService);
  private professionalApiService = inject(ProfessionalApiService);
  private readonly supportService = inject(SupportService);
  private readonly notificationService = inject(NotificationService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly pendingFreePlanWheelStorage = inject(PendingFreePlanWheelStorageService);
  private readonly router = inject(Router);

  public readonly openPlanLimitUpsell$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(planLimitReached),
        tap((event) => {
          this.dialog.open(PersonalPlanUpsellDialogComponent, resolvePlanLimitUpsellDialogConfig(event));
        })
      ),
    { dispatch: false }
  );

  savePendingFreePlanWheel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(savePendingFreePlanWheel),
      exhaustMap(({ locale }) => {
        const pending = this.pendingFreePlanWheelStorage.read();
        if (!pending) {
          return of(savePendingFreePlanWheelFailure({ error: 'Pending wheel data is unavailable.' }));
        }

        return this.wheelsApiService.createWheel(pending.wheel.name).pipe(
          switchMap((createdWheels) => {
            const wheelId = createdWheels[0]?.wheelId;
            if (!wheelId) return throwError(() => new Error('Created wheel ID is unavailable.'));

            const createCategories$ = pending.categories.length
              ? forkJoin(
                  pending.categories.map(({ label, description, color }) =>
                    this.wheelsApiService.createCategory({ label, description, color })
                  )
                )
              : of([]);

            return createCategories$.pipe(
              switchMap((createdCategories) => {
                const categoryIds = createdCategories.map(({ id }) => id);

                return this.wheelsApiService
                  .updateWheel(wheelId, { name: pending.wheel.name, categoryIds, isDefault: pending.wheel.isDefault })
                  .pipe(map((wheels) => ({ wheels, wheelId, createdCategories })));
              })
            );
          }),
          map(({ wheels, wheelId, createdCategories }) => {
            const updatedWheel = wheels.find((wheel) => wheel.wheelId === wheelId || wheel.id === wheelId);
            if (!updatedWheel?.wheelVersionId) throw new Error('Updated wheel version ID is unavailable.');

            const session: CreateSessionDto = {
              wheelId,
              wheelVersionId: updatedWheel.wheelVersionId,
              scores: createdCategories.map((category, index) => ({
                categoryId: category.id,
                value: pending.categories[index].score,
              })),
              focusAtSubmit: [],
              locale,
            };
            this.pendingFreePlanWheelStorage.saveSession(session);

            return savePendingFreePlanWheelSuccess();
          }),
          timeout(30_000),
          catchError((error: Error) => of(savePendingFreePlanWheelFailure({ error: error.message || 'Could not save your wheel.' })))
        );
      })
    )
  );

  redirectAfterPendingFreePlanWheelSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(savePendingFreePlanWheelSuccess),
      exhaustMap(() =>
        from(this.router.navigate(['/app/wheel'])).pipe(
          filter((navigated) => !navigated),
          map(() => savePendingFreePlanWheelFailure({ error: 'Could not open your new wheel.' })),
          catchError((error: Error) => of(savePendingFreePlanWheelFailure({ error: error.message || 'Could not open your new wheel.' })))
        )
      )
    )
  );

  applyPendingFreePlanScores$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setActiveWheel), // triggered by setActiveWheel in Personal component
      concatMap(({ wheel }) => {
        const session = this.pendingFreePlanWheelStorage.read()?.session;
        if (!session || session.wheelId !== wheel.id) return EMPTY;

        return from([
          ...session.scores.map(({ categoryId, value }) => setScore({ categoryId, score: value })),
          completePendingFreePlanRegistration(),
        ]);
      })
    )
  );

  finishPendingFreePlanRegistration$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(completePendingFreePlanRegistration),
        tap(() => {
          this.pendingFreePlanWheelStorage.remove();
          this.dialog.open(RegistrationCompleteDialogComponent, {
            width: '520px',
            disableClose: false,
          });
        })
      ),
    { dispatch: false }
  );

  loadFreePlanDefaultTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadDefaultTemplate),
      withLatestFrom(this.store.select(selectLang)),
      switchMap(([, locale = 'en']) => {
        const id = defaultWheelIdByLocale(locale);
        return this.wheelsApiService.fetchFreePlanDefaultTemplate(id).pipe(
          take(1),
          map((template) => {
            return loadDefaultTemplateSuccess({ wheel: wheelTemplateToState(template) });
          }),
          catchError(() =>
            this.http.get<Record<string, WheelStateCategory[]>>('assets/categories_wellbeing.json').pipe(
              map((categoriesByLocale) =>
                loadDefaultTemplateSuccess({
                  wheel: {
                    id,
                    reminder: null,
                    categories: categoriesByLocale[locale] ?? categoriesByLocale['en'] ?? [],
                    name: 'Wellbeing',
                    descriptionKey: 0,
                    wheelKey: 0,
                    focus: [],
                    focusLoading: false,
                    loading: false,
                  },
                })
              ),
              catchError(() => EMPTY)
            )
          )
        );
      })
    )
  );

  // Personal Plan effects
  loadUserWheels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserWheels),
      switchMap(() => {
        return this.wheelsApiService.fetchUserWheels().pipe(
          take(1),
          map((wheels) => loadUserWheelsSuccess({ wheels })),
          catchError((error) => {
            return of(
              loadUserWheelsFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    )
  );

  getWheelVersionById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getWheelVersionById),
      switchMap(({ id, sessionId }) => {
        return this.wheelsApiService.fetchWheelVersionById(id).pipe(
          withLatestFrom(this.store.select(selectSessions)),
          take(1),
          switchMap(([wheel, sessions]) => {
            const wheelSessions = sessions.filter((s) => s.wheelId === wheel.id);
            const session = wheelSessions.find((s) => s.id === sessionId);
            const mappedWheel = mapOutputToState(wheel, session);

            if (!mappedWheel) {
              return of(
                getWheelVersionByIdFailure({
                  error: 'Error loading user wheels',
                })
              );
            }

            return of(getWheelVersionByIdSuccess({ wheel: mappedWheel }));
          }),
          catchError((error) => {
            return of(
              getWheelVersionByIdFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    )
  );

  loadWheelByIdAndAccountId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadWheelForParticipant),
      switchMap(({ wheelId, accountId }) => {
        return this.wheelsApiService.fetchLatestVersionByAccountId(wheelId, accountId).pipe(
          take(1),
          map((wheel) => loadWheelForParticipantSuccess({ wheel })),
          catchError((error) => {
            return of(
              loadWheelForParticipantFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    )
  );

  createWheelForUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createWheelForUser),
      switchMap(() =>
        this.wheelsApiService.createWheel().pipe(
          take(1),
          map((wheels) => {
            this.notificationService.showSuccess('A wheel is successfully created!');
            return createWheelForUserSuccess({ wheels });
          }),
          catchError((error: AppError) => {
            if (error.code === APP_ERROR_CODE.wheelLimitExceeded) {
              return of(planLimitReached({ limit: 'wheels', context: 'createWheel' }), createWheelForUserFailure({ error: error.message }));
            } else if (error?.status === 0 || error?.status >= 500) {
              this.notificationService.showError('Network error. Please check your connection and try again.');
            } else {
              this.notificationService.showError(
                error.message || 'Failed to create a wheel. If error persists, please contact support - support@resourcesof.life.'
              );
            }

            return of(
              createWheelForUserFailure({
                error: error ? error.message : 'Failed to create user wheel',
              })
            );
          })
        )
      )
    )
  );

  createDefaultWheelForUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createDefaultPersonalWheel),
      withLatestFrom(this.store.select(selectLang)),
      switchMap(([, locale]) => {
        const id = defaultWheelIdByLocale(locale);
        return this.wheelsApiService.createDefaultWheel(id).pipe(
          take(1),
          map((wheels) => {
            return createWheelForUserSuccess({ wheels });
          }),
          catchError((error: AppError) => {
            if (error.code === APP_ERROR_CODE.wheelLimitExceeded) {
              return of(planLimitReached({ limit: 'wheels', context: 'createWheel' }), createWheelForUserFailure({ error: error.message }));
            } else {
              this.notificationService.showError(error.message);
            }

            return of(
              createWheelForUserFailure({
                error: error.message,
              })
            );
          })
        );
      })
    )
  );

  updatePersonalWheel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updatePersonalWheel),
      switchMap(({ wheelId, updates, skipNotify }) => {
        const categoryIds = updates.categoryIds?.map((category) => category) || [];

        return this.wheelsApiService.updateWheel(wheelId, { name: updates.name, categoryIds, isDefault: updates.isDefault }).pipe(
          take(1),
          map((wheels) => {
            if (!skipNotify) {
              this.notificationService.showSuccess(`${updates.name} is successfully updated.`);
            }

            return updatePersonalWheelSuccess({ wheels });
          }),
          catchError((error) => {
            if (!skipNotify) {
              this.notificationService.showError(error.message);
            }

            return of(
              updatePersonalWheelFailure({
                error: error?.error ? error.error.message : 'Failed to update personal wheel',
              })
            );
          })
        );
      })
    );
  });

  switchPersonalWheel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(switchPersonalWheel),
      withLatestFrom(this.store.select(selectActiveWheel), this.store.select(selectUserWheels)),
      switchMap(([{ wheel, shouldSaveSession }, activeWheel, wheels]) => {
        const switchWheelAction = updatePersonalWheel({
          wheelId: wheel.id,
          updates: {
            name: wheel.name,
            isDefault: true,
            categoryIds: wheel.categorySnapshots.map((category) => category.categoryId),
          },
          skipNotify: true,
        });

        if (!shouldSaveSession || !activeWheel) {
          return of(switchWheelAction);
        }

        const userWheel = wheels.find((item) => item.id === activeWheel.id);
        if (!userWheel) {
          return of(switchWheelAction);
        }

        const session = {
          wheelId: userWheel.id,
          wheelVersionId: userWheel.wheelVersionId,
          scores: activeWheel.categories.map((category) => ({
            categoryId: category.id,
            value: category.score,
          })),
          focusAtSubmit: activeWheel.focus,
          locale: 'en',
        };

        return of(saveSession({ activeWheel, session }), switchWheelAction);
      })
    );
  });

  deletePersonalWheel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deletePersonalWheel),
      switchMap(({ wheelId }) => {
        return this.wheelsApiService.deleteWheel(wheelId).pipe(
          take(1),
          map((wheels) => deletePersonalWheelSuccess({ wheels })),
          catchError((error) =>
            of(
              deletePersonalWheelFailure({
                error: error?.error ? error.error.message : 'Failed to delete a wheel',
              })
            )
          )
        );
      })
    );
  });

  setActiveWheel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deletePersonalWheelSuccess),
      withLatestFrom(this.store.select(selectUserWheels)),
      switchMap(([, wheels]) => {
        const wheel = mapActiveWheel(wheels);

        if (!wheel) {
          return of(setActiveWheelFailure());
        }

        return of(setActiveWheel({ wheel: wheel }));
      }),
      catchError(() => {
        return of(setActiveWheelFailure());
      })
    );
  });

  addCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addCategory),
      switchMap(({ color }) =>
        this.wheelsApiService
          .createCategory({
            label: 'New Category',
            color,
            description: '',
          })
          .pipe(
            take(1),
            map((category) => addCategorySuccess({ category })),
            catchError((error) =>
              of(
                addCategoryFailure({
                  error: error?.error ? error.error.message : 'Failed to create category',
                })
              )
            )
          )
      )
    );
  });

  editCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(editCategory),
      switchMap((category) =>
        this.wheelsApiService
          .editCategory(category.categoryId, {
            label: category.label,
            color: category.color,
            description: category.description,
          })
          .pipe(
            take(1),
            map((data) => editCategorySuccess({ category: data })),
            catchError((error) =>
              of(
                editCategoryFailure({
                  error: error?.error ? error.error.message : 'Failed to create category',
                })
              )
            )
          )
      )
    );
  });

  deleteCategory$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteCategory),
      switchMap(({ id }) =>
        this.wheelsApiService.deleteCategory(id).pipe(
          take(1),
          map(() => deleteCategorySuccess({ id })),
          catchError((error) =>
            of(
              deleteCategoryFailure({
                error: error?.error ? error.error.message : 'Failed to create category',
              })
            )
          )
        )
      )
    );
  });

  // Professional Plan effects
  loadClients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadClients),
      switchMap(() => {
        return this.professionalApiService.fetchClients().pipe(
          take(1),
          map((clients) => loadClientsSuccess({ clients })),
          catchError((error) => {
            return of(
              loadClientsFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    )
  );

  createClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createClient),
      switchMap(({ client }) => {
        return this.professionalApiService.createClient(client).pipe(
          take(1),
          map((client) => createClientSuccess({ client })),
          catchError((error) => {
            return of(
              createClientFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    )
  );

  updateClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateClient),
      switchMap(({ id, updates }) => {
        return this.professionalApiService.updateClient(id, updates).pipe(
          take(1),
          map((client) => updateClientSuccess({ client })),
          catchError((error) => {
            return of(
              updateClientFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    )
  );

  deleteClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteClient),
      switchMap(({ id }) => {
        return this.professionalApiService.deleteClient(id).pipe(
          take(1),
          map(() => deleteClientSuccess({ id })),
          catchError((error) => {
            return of(
              deleteClientFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    )
  );

  loadBrand$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBrand),
      switchMap(() => {
        return this.professionalApiService.fetchBrand().pipe(
          take(1),
          map((brand) => loadBrandSuccess({ brand })),
          catchError((error) => {
            return of(
              loadBrandFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    )
  );

  createBrand$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createBrand),
      switchMap(({ brand }) => {
        // todo add logo upload
        return this.professionalApiService.createBrand(brand).pipe(
          take(1),
          map((brand) => createBrandSuccess({ brand })),
          catchError((error) => {
            return of(
              createBrandFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    );
  });

  updateBrand$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateBrand),
      switchMap(({ brand }) => {
        return this.professionalApiService.updateBrand(brand).pipe(
          take(1),
          map((brand) => updateBrandSuccess({ brand })),
          catchError((error) => {
            return of(
              updateBrandFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    );
  });

  createLink$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createLink),
      switchMap(({ participantId, link }) => {
        return this.professionalApiService.createLink(participantId, link).pipe(
          take(1),
          map((link) => createLinkSuccess({ link })),
          catchError((error) => {
            return of(
              createLinkFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    );
  });

  deleteLink$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteLink),
      switchMap(({ id }) => {
        return this.professionalApiService.deleteLink(id).pipe(
          take(1),
          map(() => deleteLinkSuccess({ id })),
          catchError((error) => {
            return of(
              deleteLinkFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    );
  });

  assignWheel$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createLink),
      switchMap(({ participantId, assignedWheels }) => {
        return this.professionalApiService.updateClient(participantId, { assignedWheels }).pipe(
          take(1),
          map((client) => updateClientSuccess({ client })),
          catchError((error) => {
            return of(
              createLinkFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    );
  });

  saveClientFocus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveClientFocus),
      filter(({ token }) => !token),
      switchMap(({ wheelId, newFocus }) => {
        return this.wheelsApiService.createFocus(wheelId, newFocus).pipe(
          take(1),
          map((data) => {
            this.notificationService.showSuccess(`Focus saved.`);
            return saveClientFocusSuccess({ focus: data.categoryIds, wheelId });
          }),
          catchError((error) => {
            this.notificationService.showError(`Error saving focus. Please, try again.`);
            return of(
              saveClientFocusFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            );
          })
        );
      })
    )
  );

  saveClientFocusForParticipant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveClientFocusForParticipant),
      filter(({ token }) => Boolean(token)),
      switchMap(({ newFocus, token }) => {
        return this.wheelsApiService.createFocusForParticipant(newFocus, token).pipe(
          take(1),
          map((data) => saveClientFocusForParticipantSuccess({ focus: data.categoryIds })),
          catchError((error) =>
            of(
              saveClientFocusForParticipantFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            )
          )
        );
      })
    )
  );

  // todo move chain saves to the back
  saveClientFocusSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveClientFocusSuccess),
      switchMap(({ wheelId }) => {
        return this.wheelsApiService.updateWheel(wheelId, {}).pipe(
          take(1),
          map(() => updateWheelOnSaveClientFocusSuccess()),
          catchError((error) =>
            of(
              saveClientFocusFailure({
                error: error?.message ?? 'Error loading user wheels',
              })
            )
          )
        );
      })
    )
  );

  saveSession$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(saveSession),
      switchMap(({ activeWheel, session, token }) => {
        const sessionOutput: CreateSessionDto = {
          wheelId: activeWheel.id,
          wheelVersionId: session.wheelVersionId,
          scores: session.scores, // todo fix saving with zeros
          focusAtSubmit: activeWheel.focus || [],
          locale: 'en', // todo fix
          notes: activeWheel.sessionDraft?.noteMd,
        };

        if (token) {
          return this.wheelsApiService.createSessionForParticipant(sessionOutput, token);
        }

        return this.wheelsApiService.createSession(sessionOutput).pipe(
          take(1),
          map((sessions) => {
            this.notificationService.showSuccess(`Session saved.`);
            return saveSessionSuccess({ sessions });
          }),
          catchError((error: AppError) => {
            if (error.code === APP_ERROR_CODE.wheelSessionsLimitExceeded) {
              return of(planLimitReached({ limit: 'sessions', context: 'saveSession' }), saveSessionFailure({ error: error.message }));
            } else {
              this.notificationService.showError(error.message || `Error saving session.`);
            }

            return of(
              saveSessionFailure({
                error: error?.message ?? 'Error saving session',
              })
            );
          })
        );
      })
    );
  });

  reloadSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveSessionSuccess),
      map(({ sessions }) => getUserSessionsSuccess({ sessions }))
    )
  );

  resetSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveSessionSuccess),
      map(() => resetSession())
    )
  );

  showSessionSavedDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(saveSessionSuccess),
        tap(() => {
          const successDialogData: SessionSavedDialogData = {
            autoCloseTimeSeconds: 5,
          };

          this.dialog.open(SessionSavedDialogComponent, {
            data: successDialogData,
            width: '450px',
            disableClose: false,
          });
        })
      ),
    { dispatch: false }
  );

  getSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getUserSessions, deletePersonalWheelSuccess),
      switchMap(() => {
        return this.wheelsApiService.fetchSessions().pipe(
          take(1),
          map((sessions) => getUserSessionsSuccess({ sessions })),
          catchError((error) =>
            of(
              getUserSessionsFailure({
                error: error?.message ?? 'Error loading user link data',
              })
            )
          )
        );
      })
    )
  );

  deleteSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteSession),
      switchMap(({ id }) => {
        return this.wheelsApiService.deleteSession(id).pipe(
          take(1),
          map(() => {
            this.notificationService.showSuccess('Session is deleted successfully.');
            return deleteSessionSuccess({ id });
          }),
          catchError((error) => {
            this.notificationService.showError('Error deleting session. Please try again later.');
            return of(
              deleteSessionFailure({
                error: error?.message ?? 'Error deleting session',
              })
            );
          })
        );
      })
    )
  );

  getSessionsForParticipant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSessionsForParticipant),
      switchMap(({ token }) => {
        return this.wheelsApiService.fetchSessionsForParticipant(token).pipe(
          take(1),
          map((sessions) => getSessionsForParticipantSuccess({ sessions })),
          catchError((error) => {
            return of(
              getSessionsForParticipantFailure({
                error: error?.message ?? 'Error loading user link data',
              })
            );
          })
        );
      })
    )
  );

  getLinkData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getLinkDataByToken),
      switchMap(({ token }) => {
        return this.professionalApiService.fetchLinkData(token).pipe(
          take(1),
          map((data) => getLinkDataByTokenSuccess({ data })),
          catchError((error) => {
            return of(
              getLinkDataByTokenFailure({
                error: error?.message ?? 'Error loading user link data',
              })
            );
          })
        );
      })
    )
  );

  submitSupportRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitSupportRequest),
      switchMap(({ request }) => {
        return this.supportService.submitSupportRequest(request).pipe(
          map((response) => {
            this.notificationService.showSuccess("Message sent successfully. We'll get back to you as soon as possible.");
            return submitSupportRequestSuccess({ ticketId: response.ticketId });
          }),
          catchError((error: AppError) => {
            this.notificationService.showError('Could not send your message. Please try again or email us directly.');
            return of(submitSupportRequestFailure({ error: error.message }));
          })
        );
      })
    )
  );

  submitInquiryRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitInquiryRequest),
      switchMap(({ request }) => {
        return this.supportService.submitWhiteLabelRequest(request).pipe(
          map(() => {
            this.notificationService.showSuccess("Message sent successfully. We'll get back to you as soon as possible.");
            return submitInquirySuccess();
          }),
          catchError((error: AppError) => {
            this.notificationService.showError('Could not send your message. Please try again or email us directly.');
            return of(submitInquiryFailure({ error: error.message }));
          })
        );
      })
    )
  );

  submitFeatureRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitFeatureRequest),
      switchMap(({ request }) => {
        return this.supportService.submitFeatureRequest(request).pipe(
          map(() => {
            this.notificationService.showSuccess("Message sent successfully. We'll get back to you as soon as possible.");
            return submitFeatureRequestSuccess();
          }),
          catchError((error: AppError) => {
            this.notificationService.showError('Could not send your message. Please try again or email us directly.');
            return of(submitFeatureRequestFailure({ error: error.message }));
          })
        );
      })
    )
  );
}
